import { convertInchesToUkString } from '../measureHelper';
import { buildRange } from '../numericHelper';

export const BODY_TYPES = [
  'Average',
  'Slim',
  'Athletic / Toned',
  'Muscular',
  'Curvy',
  'Heavyset / Stocky',
  'Plus-Sized / Full-Figured',
  'Little Person',
] as const;

export const BODY_TYPE_OPTIONS = BODY_TYPES.map((bodyType) => ({
  value: bodyType,
  label: bodyType,
}));

export const HAIR_LENGTH = [
  'Bald',
  'Balding',
  'Short',
  'Medium',
  'Long',
  'Buzz cut',
  'Pixie Cut',
] as const;
export const HAIR_LENGTH_OPTIONS = HAIR_LENGTH.map((hairLength) => ({
  value: hairLength,
  label: hairLength,
}));

export const HAIR_COLOR = [
  'Black',
  'Brown',
  'Light Brown',
  'Blonde',
  'Red',
  'Grey',
  'White',
  'Bald',
  'Other',
] as const;
export const HAIR_COLOR_OPTIONS = HAIR_COLOR.map((hairColor) => ({
  value: hairColor,
  label: hairColor,
}));

export const EYE_COLOR = [
  'Brown',
  'Blue',
  'Green',
  'Hazel',
  'Amber',
  'Grey',
  'Red',
  'Violet',
] as const;

export const EYE_COLOR_OPTIONS = EYE_COLOR.map((eyeColor) => ({
  label: eyeColor,
  value: eyeColor,
}));

export const WARDROBE_TEXT = `Please fill out measurements as thoroughly as possible. It is important to also regularly confirm they are up to date. Missing or inaccurate measurements can prevent your profile appearing in searches for roles that you might otherwise match!`;

export const DRESS = ['00', ...buildRange(60, 0).map((num) => num.toString())];

export const CUP = [
  'AA',
  'A',
  'B',
  'C',
  'D',
  'DD',
  'DDD',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
] as const;

export const CUP_UK = [
  'AA',
  'A',
  'B',
  'C',
  'D',
  'DD',
  'E',
  'F',
  'FF',
  'G',
  'GG',
  'H',
  'HH',
  'J',
  'JJ',
  'K',
  'KK',
] as const;

export const SHOES = buildRange(20, 0, 0.5).map((num) => num.toString());
export const NECK_SIZES = buildRange(19, 13, 0.5).map((num) => num.toString());

export const SHIRT = [
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  '3XL',
  '4XL',
  '5XL',
  '6XL',
] as const;

export const ADULT_HAT_SIZE_OPTIONS = [
  '20 3/4',
  '21',
  '21 1/2',
  '22',
  '22 1/2',
  '22 3/4',
  '23',
  '23 1/2',
  '24',
  '24 3/8',
  '24 7/8',
  '25 1/8',
  '26 1/2',
] as const;

export const MINOR_HAT_SIZE_OPTIONS = [
  '19 1/8',
  '19 1/2',
  '19 3/4',
  '20 1/4',
  '20 3/4',
  '21 1/8',
  '21 1/2',
  '21 7/8',
] as const;

export const ALL_HAT_SIZE_OPTIONS = [
  '19 1/8',
  '19 1/2',
  '19 3/4',
  '20 1/4',
  '20 3/4',
  '21',
  '21 1/8',
  '21 1/2',
  '21 7/8',
  '22',
  '22 1/2',
  '22 3/4',
  '23',
  '23 1/2',
  '24',
  '24 3/8',
  '24 7/8',
  '25 1/8',
  '26 1/2',
] as const;

export const MAKE = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'] as const;

export const MODEL = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'] as const;

export const YEAR = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'] as const;

export const COLOR = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'] as const;

export const CONDITION = [
  'Excellent / New',
  'Good / Used',
  'Poor / Visible Damages',
] as const;

export const TYPE = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'] as const;

interface HeightOption {
  feetInchesLabel: string;
  inches: number;
}

export const HEIGHT_OPTIONS = Array(4)
  .fill(0)
  .reduce<HeightOption[]>((acc, _, feetIdx) => {
    const feet = feetIdx + 4; // starts from 4 to 7
    const heightsForFeet = Array(12)
      .fill(0)
      .map<HeightOption>((_, inches) => ({
        feetInchesLabel: `${feet} ft ${inches} in`,
        inches: feet * 12 + inches,
      }));
    return acc.concat(heightsForFeet);
  }, []);
export const UK_HEIGHT_OPTIONS_INCHES = HEIGHT_OPTIONS.map(
  ({ inches }) => inches
);

export const UK_HEIGHT_OPTIONS = UK_HEIGHT_OPTIONS_INCHES.map((inches) => ({
  value: inches,
  label: convertInchesToUkString(inches),
}));

export const CHEST_SIZE_OPTIONS = [
  '30',
  '32',
  '34',
  '36',
  '38',
  '40',
  '44',
] as const;

export const WAIST_SIZE_OPTIONS = [
  '28',
  '30',
  '32',
  '34',
  '36',
  '38',
  '40',
  '44',
  '46',
  '48',
] as const;

export const Reasons = [
  'Sick/Injured',
  'Unexpected matching recall on another project',
  'Transportation Issue',
  'Terms of the jobs are different than originally stated',
  'Family emergency',
  'Audition',
  'Double booked',
  'Other',
];

export const wardrobeOptionsMap = {
  dress: DRESS,
  cup: CUP,
  shoe_size: SHOES,
  shirt_size: SHIRT,
  chest: CHEST_SIZE_OPTIONS,
  us_coat_length: [],
  neck: NECK_SIZES,
};

export const getWardrobeOptions = (
  wardrobeType: string,
  isMinor = false
): readonly string[] => {
  if (wardrobeType === 'hat') {
    return isMinor ? MINOR_HAT_SIZE_OPTIONS : ADULT_HAT_SIZE_OPTIONS;
  }
  return wardrobeOptionsMap[wardrobeType];
};

export const getWardrobeOptionsUK = (
  wardrobeType: string
): readonly string[] => {
  return wardrobeOptionsMapUK[wardrobeType];
};

export const MinMaxValues = {
  hip: { min: 20, max: 60 }, // Example min and max values for Hat
  waist: { min: 10, max: 60 },
  chest_bust: { min: 20, max: 70 },
  bust: { min: 20, max: 50 },
  neck: { min: 13, max: 19 },
  sleeve: { min: 31, max: 37 },
  inseam: { min: 12, max: 40 },
  pants: { min: 0, max: 60 },
};

export const WardrobeLabel = {
  hat: 'Hat Size',
  shirt_size: 'Shirt Size',
  shoe_size: 'Shoe Size',
  waist: 'Waist Size',
  cup: 'Cup Size',
  bust: 'Bust Size',
  dress: 'Dress Size',
  hip: 'Hip Size',
  pants: 'Pant Size',
  chest_bust: 'Chest Size',
  inseam: 'Inseam Size',
  neck: 'Neck Size',
  sleeve: 'Sleeve Size',
  us_coat_length: 'Coat Length',
};

export const WardrobeMapping = {
  Hat: 'hat',
  Shirt: 'shirt_size',
  Shoe: 'shoe_size',
  Waist: 'waist',
  'Cup (Women)': 'cup',
  'Bust (Women)': 'bust',
  'Dress (Women)': 'dress',
  'Hips (Women)': 'hip',
  'Pant (Women)': 'pants',
  'Chest (Men)': 'chest_bust',
  'Inseam (Men)': 'inseam',
  'Neck (Men)': 'neck',
  'Sleeve (Men)': 'sleeve',
  'Coat Length (Men)': 'us_coat_length',
};

export const WARDROBE_LABELS_BY_GENDER = {
  Male: [
    'Hat',
    'Shirt',
    'Shoe',
    'Waist',
    'Chest (Men)',
    'Inseam (Men)',
    'Neck (Men)',
    'Sleeve (Men)',
    'Coat Length (Men)',
  ],
  Female: [
    'Hat',
    'Shirt',
    'Shoe',
    'Waist',
    'Cup (Women)',
    'Bust (Women)',
    'Dress (Women)',
    'Hips (Women)',
    'Pant (Women)',
  ],
  Other: [
    'Hat',
    'Shirt',
    'Shoe',
    'Waist',
    'Cup (Women)',
    'Bust (Women)',
    'Dress (Women)',
    'Hips (Women)',
    'Pant (Women)',
    'Chest (Men)',
    'Inseam (Men)',
    'Neck (Men)',
    'Sleeve (Men)',
  ],
  'Non-Binary': [
    'Hat',
    'Shirt',
    'Shoe',
    'Waist',
    'Cup (Women)',
    'Bust (Women)',
    'Dress (Women)',
    'Hips (Women)',
    'Pant (Women)',
    'Chest (Men)',
    'Inseam (Men)',
    'Neck (Men)',
    'Coat Length (Men)',
  ], // excludes Sleeve (Men)
};

export const wardrobeOptionsMapUK = {
  uk_dress: buildRange(26, 4, 2),
  sleeve: buildRange(40, 30, 0.5),
  neck: buildRange(21, 11, 0.5),
  chest_bust: buildRange(60, 26, 0.5),
  chest: buildRange(60, 26, 0.5),
  cup: CUP_UK,
  underbust: buildRange(50, 28, 2),
  waist: buildRange(60, 21, 0.5),
  natural_waist: buildRange(60, 21, 0.5),
  trouser_waist: buildRange(50, 26, 2),
  hip: buildRange(56, 28, 0.5),
  inseam: buildRange(40, 26, 1),
  uk_shoe: buildRange(17, 1, 0.5),
  head: buildRange(26, 19, 0.5),
};

export const MinMaxValuesUK = {};

export const WardrobeLabelUK = {
  uk_dress: 'Dress Size',
  head: 'Head Size',
  neck: 'Collar Size',
  sleeve: 'Sleeve Size',
  chest: 'Chest Size',
  chest_bust: 'Chest/Bust Size',
  cup: 'Cup Size',
  underbust: 'Underbust Size',
  waist: 'Waist Size',
  natural_waist: 'Natural Waist Size',
  trouser_waist: 'Trouser Waist Size',
  hip: 'Hips Size',
  inseam: 'Inside Leg Size',
  uk_shoe: 'Shoe Size',
};

export const WardrobeMappingUK = {
  'Dress (UK)': 'uk_dress',
  Head: 'head',
  Collar: 'neck',
  Sleeve: 'sleeve',
  'Chest (Men)': 'chest',
  'Chest/Bust (Women, Non-Binary)': 'chest_bust',
  Cup: 'cup',
  Underbust: 'underbust',
  'Waist (Women, Non-Binary)': 'waist',
  'Natural Waist (Men)': 'natural_waist',
  'Trouser Waist (Men)': 'trouser_waist',
  Hips: 'hip',
  'Inside Leg': 'inseam',
  'Shoe (UK)': 'uk_shoe',
};

export const WARDROBE_LABELS_BY_GENDER_UK = {
  Male: [
    'Head',
    'Collar',
    'Sleeve',
    'Chest (Men)',
    'Natural Waist (Men)',
    'Trouser Waist (Men)',
    'Inside Leg',
    'Shoe (UK)',
  ],
  Female: [
    'Dress (UK)',
    'Head',
    'Collar',
    'Chest/Bust (Women, Non-Binary)',
    'Cup',
    'Underbust',
    'Waist (Women, Non-Binary)',
    'Hips',
    'Shoe (UK)',
  ],
  Other: [
    'Dress (UK)',
    'Head',
    'Collar',
    'Sleeve',
    'Chest/Bust (Women, Non-Binary)',
    'Cup',
    'Underbust',
    'Waist (Women, Non-Binary)',
    'Hips',
    'Inside Leg',
    'Shoe (UK)',
  ],
  'Non-Binary': [
    'Dress (UK)',
    'Head',
    'Collar',
    'Sleeve',
    'Chest/Bust (Women, Non-Binary)',
    'Cup',
    'Underbust',
    'Waist (Women, Non-Binary)',
    'Hips',
    'Inside Leg',
    'Shoe (UK)',
  ],
};

export const WORK_DOCUMENTS_PROFILE_SECTION_TITLE =
  'Work Documents & Bank Details';

export const MAX_PROFILE_PHOTOS = 12;
